import { useState, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const C = {
  navy:"#0f2137", amber:"#f59e0b", green:"#10b981", red:"#ef4444", yellow:"#f59e0b",
  text:"#1e293b", muted:"#64748b", border:"#e2e8f0", bg:"#f8fafc"
};

const s = {
  page:{ fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", background:C.bg, minHeight:"100vh", color:C.text },
  header:{ background:C.navy, padding:"14px 20px", display:"flex", alignItems:"center", gap:10 },
  logo:{ color:"#fff", fontWeight:900, fontSize:"1.25rem", letterSpacing:"-0.5px", textTransform:"uppercase" },
  card:{ background:"#fff", border:`1px solid ${C.border}`, borderRadius:14, padding:22, marginBottom:18 },
  input:{ width:"100%", padding:"10px 13px", border:`2px solid ${C.border}`, borderRadius:9, fontSize:"0.875rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  textarea:{ width:"100%", padding:"11px 13px", border:`2px solid ${C.border}`, borderRadius:9, fontSize:"0.875rem", fontFamily:"inherit", resize:"vertical", outline:"none", lineHeight:1.6, boxSizing:"border-box" },
  btn:{ background:C.amber, color:C.navy, fontWeight:800, padding:"12px 24px", borderRadius:9, border:"none", cursor:"pointer", fontSize:"0.9rem" },
  btnSm:{ background:C.navy, color:"#fff", fontWeight:700, padding:"8px 15px", borderRadius:8, border:"none", cursor:"pointer", fontSize:"0.78rem" },
  btnGhost:{ background:"transparent", color:C.muted, fontWeight:600, padding:"7px 13px", borderRadius:8, border:`1px solid ${C.border}`, cursor:"pointer", fontSize:"0.78rem" },
  badge:(t)=>{ const m={green:["#d1fae5","#065f46"],red:["#fee2e2","#991b1b"],yellow:["#fef3c7","#92400e"],gray:["#f1f5f9","#475569"],blue:["#dbeafe","#1e40af"],purple:["#ede9fe","#5b21b6"]}; const[bg,col]=m[t]||m.gray; return{display:"inline-block",background:bg,color:col,fontSize:"0.7rem",fontWeight:700,padding:"3px 8px",borderRadius:6,whiteSpace:"nowrap"}; },
  h2:{ fontSize:"1.05rem", fontWeight:800, color:C.navy, marginBottom:4 },
  sub:{ fontSize:"0.83rem", color:C.muted, marginBottom:18, lineHeight:1.6 },
};

const GLOSSARY = {
  "drip edge":{ plain:"A metal strip along roof edges that directs water into gutters and away from the fascia board.", why:"Without it, water creeps under shingles and rots the wood beneath. Cheap to include, often skipped.", goodSign:"Included, material specified (aluminum or galvanized steel)", redFlag:"Not mentioned — ask if it's included" },
  "flashing":{ plain:"Thin metal pieces installed wherever your roof meets a wall, chimney, skylight, or valley.", why:"Most roof leaks happen at flashing points. Cheap or missing flashing = future leaks.", goodSign:"Step flashing and counter flashing both specified", redFlag:"Just 'flashing included' with no detail — ask what type" },
  "pipe boot":{ plain:"The rubber or metal collar that seals around pipes sticking up through your roof.", why:"Rubber boots crack in 10–15 years. Lead boots last much longer.", goodSign:"Lead boots or high-quality rubber (Bullet Boot brand) specified", redFlag:"Not mentioned, or just 'rubber boots' with no brand" },
  "underlayment":{ plain:"A protective layer installed on the roof deck under the shingles — your backup if water gets under shingles.", why:"Synthetic is far superior to felt — lighter, stronger, won't tear during install.", goodSign:"Synthetic underlayment with brand name specified", redFlag:"Just 'felt paper' or no mention at all" },
  "ice and water shield":{ plain:"A peel-and-stick waterproof membrane applied to the most vulnerable roof areas — eaves and valleys.", why:"Prevents water forced upward under shingles. Many low bids include code minimum only.", goodSign:"Coverage area specified (eaves + valleys, or full deck)", redFlag:"'Eaves only' or not mentioned — ask about valley coverage" },
  "ridge vent":{ plain:"A ventilation strip along the peak of your roof that lets hot air escape from your attic.", why:"Without it, heat and moisture build up, voiding shingle warranties and causing mold.", goodSign:"Linear feet specified with a brand name", redFlag:"No mention of ventilation — this is a required question" },
  "ridge cap":{ plain:"The shingles covering the peak of your roof — cheap cut shingles vs. proper hip-and-ridge shingles.", why:"Hip-and-ridge shingles seal and look better. Cut 3-tab shingles as ridge cap is a quality shortcut.", goodSign:"'Hip and ridge shingles' or brand name like GAF Seal-A-Ridge", redFlag:"'Ridge cap included' with no detail — ask what type" },
  "valley treatment":{ plain:"How the V-channel where two roof slopes meet is handled: open metal, closed-cut, or woven.", why:"Open metal valleys are most durable. Woven is cheapest and most prone to leaks.", goodSign:"'Open metal valley' specified", redFlag:"No mention, or 'woven' — ask which method" },
  "decking":{ plain:"The plywood or OSB boards nailed to your roof structure that everything else attaches to.", why:"Low bids include few replacement sheets, then charge premium per sheet during the job.", goodSign:"Number of replacement sheets included with per-sheet price stated upfront", redFlag:"No mention of decking replacement — ask 'what happens if you find rot?'" },
  "tear-off":{ plain:"Removing old shingles completely before new ones go on. The alternative is 'overlay' — new over old.", why:"Going over existing shingles hides problems, adds weight, and voids most warranties.", goodSign:"Full tear-off of all layers specified", redFlag:"'Going over existing' or no mention — this is non-negotiable" },
  "magnetic roller":{ plain:"A large magnet rolled across your lawn and driveway after the job to collect roofing nails.", why:"Hundreds of nails fall during a re-roof. Without cleanup, you'll find them in tires and feet for years.", goodSign:"Explicitly mentioned in the proposal", redFlag:"Not mentioned — add to your follow-up questions" },
  "soft close":{ plain:"Hardware on cabinet hinges and drawer slides that prevents slamming.", why:"Standard expectation in remodels. Worth $3–8/cabinet from suppliers, sometimes charged as upgrade.", goodSign:"'Soft-close hinges and drawer slides included' — both specified", redFlag:"Only hinges mentioned, not drawer slides" },
  "semi-custom cabinets":{ plain:"Cabinets built to order with more size and finish options than stock, less than full custom.", why:"Difference between stock, semi-custom, and custom is enormous in quality and price.", goodSign:"Brand, line name, and box construction (plywood vs. particleboard) specified", redFlag:"Just 'new cabinets' — insist on brand and line" },
  "plywood box":{ plain:"Cabinet carcasses built from real plywood instead of particleboard.", why:"Plywood holds screws better, handles moisture better, lasts 2–3x longer than particleboard.", goodSign:"'Plywood box construction' explicitly stated", redFlag:"No mention of box material — ask directly" },
  "allowance":{ plain:"A dollar amount in the quote for items not yet specified — usually materials you'll choose later.", why:"Allowances are the #1 source of budget overruns. A $500 tile allowance hides real costs.", goodSign:"Specific material named rather than an allowance", redFlag:"Any allowance without per-unit cost and realistic square footage" },
  "waterproofing system":{ plain:"Membrane or coating applied to shower walls/floors before tile to block water from the structure behind.", why:"Without it, water causes mold and rot within 5 years. Cement board alone is NOT waterproofing.", goodSign:"Specific brand named — Schluter, Wedi, RedGard", redFlag:"'Cement board' only, or no waterproofing method — deal-breaker" },
  "pressure-balance valve":{ plain:"The shower valve that prevents scalding if someone flushes a toilet. Required by code since 2004.", why:"Every shower since 2004 requires this by code. Skipping it won't pass inspection.", goodSign:"Brand specified (Moen, Delta, Kohler)", redFlag:"No mention of valve brand or type — ask explicitly" },
  "cement board":{ plain:"A rigid cement-and-fiberglass panel used behind tile in wet areas.", why:"Regular drywall will mold and rot behind tile within a few years in a wet shower.", goodSign:"Cement board specified for all shower walls", redFlag:"No mention of what's behind the tile — ask directly" },
  "polymeric sand":{ plain:"Sand mixed with binding agents swept into paver gaps and hardened with water.", why:"Regular sand washes out with every rain, letting weeds grow and pavers shift.", goodSign:"'Polymeric sand' with a brand name (Techniseal, Alliance Gator, Sakrete)", redFlag:"Just 'sand' or 'joint sand' — ask if it's polymeric" },
  "edge restraint":{ plain:"A border installed around the perimeter of a paver patio to hold outer pavers in place.", why:"Without it, outer pavers slowly migrate outward over time, loosening the whole patio.", goodSign:"Brand specified (Snap Edge, Bison, or similar)", redFlag:"Not mentioned — ask 'what holds the edge pavers in place?'" },
  "base":{ plain:"Compacted gravel layers underneath pavers — the foundation your patio is built on.", why:"Thin or poorly compacted base causes settling, shifting, and cracking. Invisible after install.", goodSign:"Depth in inches (4\" min foot traffic, 6\"+ vehicles), material specified", redFlag:"No base depth mentioned — must-ask" },
  "geotextile fabric":{ plain:"Permeable landscape fabric between soil and the gravel base of a paver project.", why:"Without it, gravel slowly sinks into soil, causing the patio to settle within a few years.", goodSign:"Mentioned in the proposal", redFlag:"Not mentioned — ask 'are you including landscape fabric under the base?'" },
  "b&b":{ plain:"'Balled and burlapped' — large trees/shrubs harvested with root ball intact, wrapped in burlap.", why:"B&B plants are more established and install faster. Cost more but give instant landscape.", goodSign:"B&B specified for trees over 2\" caliper", redFlag:"Large trees listed without size specification" },
  "drip irrigation":{ plain:"Watering system that delivers water slowly and directly to plant root zones.", why:"Uses 30–50% less water than spray, reduces disease by keeping foliage dry.", goodSign:"'Drip irrigation for all planting beds' specified", redFlag:"Just 'irrigation included' — ask drip or spray" },
  "smart controller":{ plain:"A sprinkler timer that uses local weather data to skip watering when it rains.", why:"Saves 30–50% on outdoor water bills. Prevents overwatering, the #1 killer of new plants.", goodSign:"Brand specified (Rachio, Hunter HC, Rain Bird)", redFlag:"'Timer included' with no mention of smart/weather-based" },
  "caliper":{ plain:"Tree trunk diameter measured 6 inches above ground — used to specify tree size.", why:"A 1.5\" and 3\" caliper tree look very different and cost very different amounts.", goodSign:"Caliper stated for every tree (e.g. '2.5\" caliper Red Maple')", redFlag:"Just 'maple tree' or 'large tree' — insist on caliper" },
  "egress window":{ plain:"A window large enough for escape in an emergency. Required by code for any bedroom.", why:"Without it, a basement room cannot legally be called a bedroom — affects resale and insurance.", goodSign:"Explicitly included if bedroom planned, with dimensions specified", redFlag:"Bedroom planned but no mention of egress — code violation" },
  "vapor barrier":{ plain:"Plastic sheet or coating on basement walls/floor to prevent moisture seeping through concrete.", why:"Without it, moisture gets into framing and drywall, causing mold.", goodSign:"Specified for both floor and walls", redFlag:"Not mentioned — ask 'what are you doing for moisture control?'" },
  "level 5 drywall":{ plain:"Highest drywall finish — entire surface skim-coated for a perfectly smooth result.", why:"Lower levels show seams under paint sheen or raking light.", goodSign:"'Level 5 finish' explicitly stated", redFlag:"Just 'drywall included' — ask what finish level" },
};

const BENCHMARKS = {
  roof: [
    { item:"Shingles (3-tab, installed)", low:80, high:150, unit:"per square (100 SF)", note:"Builder grade — most homeowners should upgrade" },
    { item:"Shingles (architectural/dimensional, installed)", low:150, high:300, unit:"per square", note:"Current standard quality" },
    { item:"Shingles (premium/impact-rated, installed)", low:300, high:500, unit:"per square", note:"Best for storm-prone areas; may reduce insurance premiums" },
    { item:"Decking replacement (plywood)", low:70, high:130, unit:"per sheet (4x8)", note:"Rot discovery is common — ask per-sheet price upfront" },
    { item:"Drip edge", low:1, high:3, unit:"per LF installed", note:"Should be included — red flag if charged separately" },
    { item:"Ice & water shield", low:1.50, high:3.50, unit:"per SF", note:"Eaves only is code minimum; valleys add protection" },
    { item:"Synthetic underlayment", low:0.15, high:0.40, unit:"per SF", note:"Superior to felt; should be standard" },
    { item:"Pipe boot (rubber)", low:20, high:45, unit:"each installed", note:"10–15 year life; lead boots last longer" },
    { item:"Ridge vent", low:2, high:5, unit:"per LF installed", note:"Proper ventilation protects shingle warranty" },
    { item:"Full roof replacement (avg home, architectural shingles)", low:8000, high:20000, unit:"total", note:"Varies by roof size, pitch, and region" },
  ],
  kitchen: [
    { item:"Stock cabinets (installed)", low:150, high:380, unit:"per linear foot", note:"Lowest cost; limited sizes and finishes" },
    { item:"Semi-custom cabinets (installed)", low:380, high:750, unit:"per linear foot", note:"Best value — more options, better quality" },
    { item:"Custom cabinets (installed)", low:750, high:1500, unit:"per linear foot", note:"Made to exact spec; longest lead time" },
    { item:"Quartz countertop (installed)", low:65, high:150, unit:"per SF", note:"Varies by brand and edge profile" },
    { item:"Granite countertop (installed)", low:50, high:130, unit:"per SF", note:"Natural stone — price varies by slab" },
    { item:"LVP flooring (installed)", low:5, high:12, unit:"per SF", note:"Quality range is enormous — ask for brand and wear layer" },
    { item:"Tile backsplash (installed, labor only)", low:15, high:40, unit:"per SF", note:"Labor-only if homeowner supplies tile" },
    { item:"Kitchen remodel (mid-range, full)", low:30000, high:80000, unit:"total", note:"Full gut remodel in average-sized kitchen" },
    { item:"Kitchen remodel (minor refresh)", low:10000, high:25000, unit:"total", note:"New fronts, countertops, paint — existing layout stays" },
  ],
  bathroom: [
    { item:"Full bathroom remodel (average)", low:10000, high:30000, unit:"total", note:"Highly variable based on tile and fixture quality" },
    { item:"Shower tile installation (labor)", low:15, high:35, unit:"per SF", note:"Complex patterns and smaller tiles cost more" },
    { item:"Shower door (frameless)", low:1000, high:3000, unit:"installed", note:"Most impactful aesthetic upgrade" },
    { item:"Vanity installation (labor)", low:150, high:400, unit:"each", note:"Excludes vanity cost — add $300–$3,000 for vanity itself" },
    { item:"Waterproofing (Schluter system)", low:8, high:15, unit:"per SF of shower", note:"Worth every penny — prevents the most expensive failures" },
    { item:"Exhaust fan replacement", low:150, high:400, unit:"installed", note:"Code requires ventilation — should be in every bid" },
  ],
  hardscaping: [
    { item:"Concrete pavers (installed)", low:15, high:30, unit:"per SF", note:"Includes base prep, setting, and joint sand" },
    { item:"Natural stone pavers (installed)", low:25, high:60, unit:"per SF", note:"Travertine, bluestone, flagstone — varies by stone" },
    { item:"Poured concrete patio (installed)", low:8, high:18, unit:"per SF", note:"Basic broom to stamped — finish determines price" },
    { item:"Polymeric joint sand", low:25, high:45, unit:"per bag (~50 SF)", note:"Should be standard — not regular sand" },
    { item:"Retaining wall (concrete block)", low:25, high:60, unit:"per SF of face", note:"Taller walls and curves cost more" },
    { item:"Patio base prep (labor + gravel)", low:3, high:8, unit:"per SF", note:"Includes excavation, fabric, and compacted gravel" },
  ],
  basement: [
    { item:"Basement finishing (per SF)", low:30, high:75, unit:"per SF", note:"Lower = basic; upper = full bath, bar, bedroom" },
    { item:"Egress window (installed)", low:2500, high:6000, unit:"each", note:"Includes excavation and well — required for legal bedroom" },
    { item:"Basement bathroom (full)", low:5000, high:15000, unit:"total", note:"Highly variable based on finishes" },
    { item:"Drop ceiling (installed)", low:3, high:7, unit:"per SF", note:"Cheaper and allows access to utilities" },
    { item:"LVP flooring for basement (installed)", low:5, high:12, unit:"per SF", note:"Best choice for basements due to moisture resistance" },
    { item:"Spray foam insulation (exterior walls)", low:1.50, high:4.50, unit:"per SF", note:"Superior vapor barrier and insulation in one" },
  ],
  landscaping: [
    { item:"Sod installation", low:1.50, high:4, unit:"per SF installed", note:"Includes grading and starter fertilizer" },
    { item:"Mulch (installed)", low:65, high:120, unit:"per cubic yard", note:"Covers ~160 SF at 2\"; 3\" recommended" },
    { item:"Irrigation system (per zone)", low:400, high:800, unit:"per zone installed", note:"Smart controller adds $150–$400" },
    { item:"Landscape lighting (low-voltage)", low:100, high:300, unit:"per fixture installed", note:"LED fixtures use minimal power" },
    { item:"Steel edging", low:3, high:7, unit:"per LF installed", note:"Plastic edging is $1–$2 but fails quickly" },
    { item:"5-gallon shrub (installed)", low:60, high:120, unit:"each", note:"Includes plant, labor, soil amendment" },
    { item:"2.5\" caliper shade tree (installed)", low:450, high:900, unit:"each", note:"Includes planting and soil amendment; excludes staking" },
  ],
};

function Term({ children }) {
  const term = children.toString().toLowerCase();
  const def = GLOSSARY[term];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!def) return <span style={{fontWeight:600}}>{children}</span>;

  return (
    <span ref={ref} style={{position:"relative",display:"inline"}}>
      <span onClick={() => setOpen(o => !o)}
        style={{ fontWeight:700, color:C.navy, borderBottom:`2px dashed ${C.amber}`, cursor:"pointer", paddingBottom:1 }}
        title="Tap to learn what this means"
      >{children} <span style={{fontSize:"0.65rem",color:C.amber,fontWeight:700}}>?</span></span>

      {open && (
        <span style={{
          position:"absolute", bottom:"calc(100% + 8px)", left:"50%", transform:"translateX(-50%)",
          background:"#fff", border:`1px solid ${C.border}`, borderRadius:12, padding:16,
          width:280, zIndex:200, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", display:"block"
        }}>
          <span style={{position:"absolute",bottom:-7,left:"50%",transform:"translateX(-50%)",width:12,height:12,background:"#fff",border:`1px solid ${C.border}`,borderTop:"none",borderLeft:"none",rotate:"45deg",display:"block"}}/>
          <div style={{fontWeight:800,color:C.navy,fontSize:"0.88rem",marginBottom:8,textTransform:"capitalize"}}>{children}</div>
          <p style={{fontSize:"0.82rem",color:C.text,lineHeight:1.6,margin:"0 0 10px"}}>{def.plain}</p>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:9}}>
            <div style={{fontSize:"0.72rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:5}}>Why it matters</div>
            <p style={{fontSize:"0.8rem",color:C.text,lineHeight:1.5,margin:"0 0 8px"}}>{def.why}</p>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:4}}>
              {def.goodSign&&<div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"5px 9px",fontSize:"0.72rem",color:"#15803d"}}>✓ Good: {def.goodSign}</div>}
              {def.redFlag&&<div style={{background:"#fff1f2",border:"1px solid #fecdd3",borderRadius:8,padding:"5px 9px",fontSize:"0.72rem",color:"#be123c"}}>⚠ Watch for: {def.redFlag}</div>}
            </div>
          </div>
          <button onClick={()=>setOpen(false)} style={{position:"absolute",top:8,right:10,background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:"1rem"}}>×</button>
        </span>
      )}
    </span>
  );
}

function CostBenchmarks({ schemaKey }) {
  const items = BENCHMARKS[schemaKey] || [];
  const [filter, setFilter] = useState("");
  const filtered = items.filter(i => i.item.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div>
      <div style={{...s.card, background:"#fffbeb", border:"1px solid #fde68a", marginBottom:12}}>
        <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
          <span style={{fontSize:"1.1rem"}}>💡</span>
          <div>
            <div style={{fontWeight:700,color:C.navy,fontSize:"0.88rem",marginBottom:3}}>How to use these benchmarks</div>
            <p style={{fontSize:"0.81rem",color:C.text,lineHeight:1.6,margin:0}}>National average ranges (2024–2025). Your local market may be 15–30% higher or lower. A quote significantly below the low end usually means something is missing or lower quality.</p>
          </div>
        </div>
      </div>
      <input value={filter} onChange={e=>setFilter(e.target.value)} style={{...s.input, marginBottom:14}} placeholder="Search costs (e.g. 'shingles', 'countertop')…"/>
      <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:13,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.82rem"}}>
          <thead>
            <tr style={{background:C.bg}}>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Item</th>
              <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>Low</th>
              <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>High</th>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Unit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item,i)=>(
              <tr key={i} style={{borderBottom:i<filtered.length-1?`1px solid ${C.border}`:"none"}}>
                <td style={{padding:"11px 14px"}}>
                  <div style={{fontWeight:600,color:C.navy,fontSize:"0.83rem"}}>{item.item}</div>
                  <div style={{fontSize:"0.73rem",color:C.muted,marginTop:2,lineHeight:1.5}}>{item.note}</div>
                </td>
                <td style={{padding:"11px 14px",textAlign:"center"}}><span style={{fontWeight:700,color:C.green,fontSize:"0.88rem"}}>${item.low.toLocaleString()}</span></td>
                <td style={{padding:"11px 14px",textAlign:"center"}}><span style={{fontWeight:700,color:C.red,fontSize:"0.88rem"}}>${item.high.toLocaleString()}</span></td>
                <td style={{padding:"11px 14px",fontSize:"0.78rem",color:C.muted}}>{item.unit}</td>
              </tr>
            ))}
            {filtered.length===0&&(<tr><td colSpan={4} style={{padding:24,textAlign:"center",color:C.muted,fontSize:"0.83rem"}}>No matching items found.</td></tr>)}
          </tbody>
        </table>
      </div>
      <div style={{fontSize:"0.72rem",color:C.muted,marginTop:10,textAlign:"center"}}>National averages 2024–2025 · RSMeans, HomeAdvisor, and contractor survey data · Verify with local contractors</div>
    </div>
  );
}

const SCHEMAS = {
  roof: {
    label:"Roof Replacement", icon:"🏠",
    categories:[
      { name:"Shingles & Materials", fields:[
        { key:"shingleBrand",       label:"Shingle Brand & Line",       why:"Same '30-year shingle' can vary $2,000+ in quality" },
        { key:"shingleWarranty",    label:"Shingle Warranty",           why:"Lifetime vs. 25-year — manufacturer or contractor?" },
        { key:"windRating",         label:"Wind Rating (MPH)",          why:"Critical in storm-prone areas" },
        { key:"impactRating",       label:"Impact Rating (Class 1–4)",  why:"Class 4 can reduce insurance premiums" },
        { key:"underlayment",       label:"Underlayment Type & Brand",  why:"Synthetic is far superior to felt" },
        { key:"iceWaterShield",     label:"Ice & Water Shield Coverage",why:"Eaves-only is code minimum; valleys add real protection" },
      ]},
      { name:"Decking & Structure", fields:[
        { key:"tearOffLayers",      label:"Tear-Off (layers removed)",  why:"Going over existing shingles voids warranties" },
        { key:"deckingAllowance",   label:"Decking Allowance (sheets)", why:"Biggest budget surprise — get per-sheet price now" },
        { key:"valleyTreatment",    label:"Valley Treatment",           why:"Open metal is premium; woven is cheapest and leaks most" },
        { key:"ridgeCap",           label:"Ridge Cap Type",             why:"Hip & ridge shingles seal better than cut 3-tab" },
        { key:"ridgeVent",          label:"Ridge Vent (LF)",            why:"Required for shingle warranty validity" },
      ]},
      { name:"Flashing & Details", fields:[
        { key:"dripEdge",           label:"Drip Edge",                  why:"Often omitted; causes fascia rot" },
        { key:"pipeBoot",           label:"Pipe Boot Material",         why:"Rubber fails in 10 years; lead lasts much longer" },
        { key:"flashing",           label:"Flashing Material",          why:"Aluminum vs. galvanized vs. copper" },
      ]},
      { name:"Logistics", fields:[
        { key:"permit",             label:"Permit",                     why:"Required everywhere; skipping = your liability" },
        { key:"magneticRoller",     label:"Magnetic Nail Cleanup",      why:"Nails in lawn/driveway = liability" },
        { key:"dumpster",           label:"Debris Disposal",            why:"Some low bids leave debris for you" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
        { key:"workmanshipWarranty",label:"Workmanship Warranty",       why:"1 year = minimum; 10 years = confident contractor" },
      ]},
    ],
    questions:[
      { text:"Will you pull the permit, and is that included in the price?", terms:[] },
      { text:"Are you tearing off the existing shingles or going over them?", terms:["tear-off"] },
      { text:"What brand and product line of shingles are you quoting?", terms:[] },
      { text:"How many sheets of decking are included? What's the per-sheet price if you find rot?", terms:["decking"] },
      { text:"What underlayment are you using — felt or synthetic?", terms:["underlayment"] },
      { text:"Are you replacing the drip edge, flashing, and pipe boots?", terms:["drip edge","flashing","pipe boot"] },
      { text:"What ice and water shield are you installing, and where?", terms:["ice and water shield"] },
      { text:"What ridge vent system are you using?", terms:["ridge vent"] },
      { text:"What valley treatment method are you using?", terms:["valley treatment"] },
      { text:"Will you use a magnetic roller for nail cleanup?", terms:["magnetic roller"] },
      { text:"What's your payment schedule — how much up front?", terms:[] },
      { text:"What workmanship warranty do you provide?", terms:[] },
    ],
  },
  kitchen: {
    label:"Kitchen Remodel", icon:"🍳",
    categories:[
      { name:"Cabinets", fields:[
        { key:"cabinetType",        label:"Cabinet Type",               why:"Stock / Semi-custom / Custom — enormous quality range" },
        { key:"cabinetBrand",       label:"Cabinet Brand & Line",       why:"Determines box construction, warranty, lead time" },
        { key:"boxConstruction",    label:"Box Construction",           why:"Plywood lasts decades longer than particleboard" },
        { key:"softClose",          label:"Soft-Close Hardware",        why:"Ask if both hinges AND drawer slides are included" },
        { key:"cabinetLeadTime",    label:"Cabinet Lead Time",          why:"6–16 week lead times affect your project timeline" },
      ]},
      { name:"Countertops & Surfaces", fields:[
        { key:"countertopMaterial", label:"Countertop Material & Brand",why:"'Stone option' is not a spec — brands vary $20–$50/SF" },
        { key:"countertopEdge",     label:"Edge Profile",               why:"Decorative edges add $10–$20/LF" },
        { key:"backsplashMaterial", label:"Backsplash Material or Allowance", why:"$500 allowance hides what tile actually costs" },
        { key:"backsplashLabor",    label:"Backsplash Labor Included",  why:"Often omitted when tile is owner-supplied" },
      ]},
      { name:"Plumbing & Electrical", fields:[
        { key:"plumbingScope",      label:"Plumbing Scope",             why:"'Plumbing included' can mean just reconnecting the sink" },
        { key:"electricalScope",    label:"Electrical Scope",           why:"Under-cabinet lighting often excluded" },
        { key:"permits",            label:"Permits",                    why:"Required for new circuits/plumbing work" },
      ]},
      { name:"Finishes & Demo", fields:[
        { key:"flooringMaterial",   label:"Flooring Material & Brand",  why:"LVP quality ranges $1.50–$6/SF before install" },
        { key:"demoScope",          label:"Demo & Debris Disposal",     why:"Low bids often leave tearout for you to haul" },
        { key:"drywallRepair",      label:"Drywall Repair & Paint",     why:"Gut remodels always need drywall work — often excluded" },
        { key:"applianceInstall",   label:"Appliance Installation",     why:"Often excluded even when appliances are yours" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
      ]},
    ],
    questions:[
      { text:"Is this a full gut or will you work around existing plumbing and electrical?", terms:[] },
      { text:"Are cabinets semi-custom, custom, or stock? What brand and line?", terms:["semi-custom cabinets"] },
      { text:"Are the cabinet boxes plywood or particleboard?", terms:["plywood box"] },
      { text:"Is soft-close hardware included — both hinges and drawer slides?", terms:["soft close"] },
      { text:"Who is supplying countertops, and what's the edge profile?", terms:["allowance"] },
      { text:"Is backsplash tile supplied by you, or is it an allowance?", terms:["allowance"] },
      { text:"Are permits included for electrical and plumbing work?", terms:[] },
      { text:"Is appliance installation included?", terms:[] },
      { text:"What's the lead time on cabinets from order to delivery?", terms:[] },
    ],
  },
  bathroom: {
    label:"Bathroom Remodel", icon:"🛁",
    categories:[
      { name:"Scope & Waterproofing", fields:[
        { key:"remodelScope",       label:"Scope",                      why:"Full gut vs. surface remodel vs. shower only" },
        { key:"waterproofing",      label:"Waterproofing System",       why:"Most critical quality spec in any shower" },
        { key:"subfloor",           label:"Subfloor Replacement",       why:"Wet bathrooms almost always have subfloor damage" },
        { key:"drywallType",        label:"Drywall Type",               why:"Regular drywall behind tile = mold in 3–5 years" },
      ]},
      { name:"Tile & Surfaces", fields:[
        { key:"floorTile",          label:"Floor Tile",                 why:"Material, size — allowances hide cost variation" },
        { key:"wallTile",           label:"Wall/Shower Tile",           why:"Ask if it's an allowance or specified" },
        { key:"tileLabor",          label:"Tile Labor (all areas)",     why:"Often excluded for floor when owner supplies tile" },
        { key:"showerDoor",         label:"Shower Door/Enclosure",      why:"Frameless to curtain rod = $300 to $3,000+" },
      ]},
      { name:"Fixtures & Fittings", fields:[
        { key:"showerValve",        label:"Shower Valve Brand",         why:"Pressure-balance is code minimum" },
        { key:"vanity",             label:"Vanity",                     why:"Supplied / allowance / owner-furnished" },
        { key:"fixtures",           label:"Fixtures (faucet, showerhead, toilet)", why:"Supplied / allowance / owner — ask for each" },
        { key:"exhaustFan",         label:"Exhaust Fan",                why:"Code requires ventilation; often skipped" },
        { key:"permit",             label:"Permit",                     why:"Required for plumbing and electrical" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
      ]},
    ],
    questions:[
      { text:"Is this a full gut to studs or a surface-level remodel?", terms:[] },
      { text:"Are you waterproofing the shower walls — what system?", terms:["waterproofing system"] },
      { text:"What tile is included — material, size, and who's supplying?", terms:["allowance"] },
      { text:"Are you replacing the subfloor if you find damage?", terms:[] },
      { text:"What shower valve brand are you quoting?", terms:["pressure-balance valve"] },
      { text:"Is the vanity supplied by you or an allowance?", terms:["allowance"] },
      { text:"Is the exhaust fan being replaced?", terms:[] },
      { text:"Are permits included?", terms:[] },
    ],
  },
  basement: {
    label:"Basement Finishing", icon:"🏗️",
    categories:[
      { name:"Framing & Insulation", fields:[
        { key:"framingMaterial",    label:"Framing Material",           why:"Steel stud doesn't mold; wood is cheaper but vulnerable" },
        { key:"exteriorInsulation", label:"Exterior Wall Insulation",   why:"R-value is code-dependent; spray foam is superior" },
        { key:"vaporBarrier",       label:"Vapor Barrier",              why:"Critical in wet climates — omitting causes mold" },
        { key:"egressWindow",       label:"Egress Window",              why:"Required for any legal bedroom" },
      ]},
      { name:"Drywall & Ceiling", fields:[
        { key:"drywallFinishLevel", label:"Drywall Finish Level",       why:"Level 5 required for gloss paint; low bids do level 3" },
        { key:"ceilingType",        label:"Ceiling Type",               why:"Drop vs. drywall vs. open — major cost difference" },
      ]},
      { name:"Electrical & Mechanical", fields:[
        { key:"electricalCircuits", label:"Number of New Circuits",     why:"Basements are often severely underspecified" },
        { key:"hvacExtension",      label:"HVAC Extension",             why:"Huge omission — basement will be unusable without it" },
      ]},
      { name:"Finishes", fields:[
        { key:"flooringMaterial",   label:"Flooring Material",          why:"LVP is standard; carpet traps moisture in basements" },
        { key:"bathroomScope",      label:"Bathroom Scope",             why:"Rough-in only vs. full finish is a massive difference" },
        { key:"permit",             label:"Permit",                     why:"Always required — no exceptions" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
      ]},
    ],
    questions:[
      { text:"Are you framing with steel stud or wood?", terms:[] },
      { text:"What insulation are you using on exterior walls — type and R-value?", terms:[] },
      { text:"Is a vapor barrier included on the floor and walls?", terms:["vapor barrier"] },
      { text:"What drywall finish level are you quoting?", terms:["level 5 drywall"] },
      { text:"Is an egress window required and included?", terms:["egress window"] },
      { text:"How many new electrical circuits are included?", terms:[] },
      { text:"Are you adding a bathroom — rough-in only or full finish?", terms:[] },
      { text:"Is the HVAC extension included?", terms:[] },
      { text:"Is a permit included?", terms:[] },
    ],
  },
  hardscaping: {
    label:"Hardscaping / Patio", icon:"🪨",
    categories:[
      { name:"Materials", fields:[
        { key:"primaryMaterial",    label:"Primary Material & Brand",   why:"'Pavers' can mean $3/SF or $18/SF material" },
        { key:"materialThickness",  label:"Material Thickness",         why:"Thin pavers crack under vehicle loads" },
        { key:"squareFootage",      label:"Square Footage in Bid",      why:"Measure yourself and verify" },
      ]},
      { name:"Base & Installation", fields:[
        { key:"baseDepth",          label:"Gravel Base Depth",          why:"4\" min for foot traffic; 6\"+ for vehicles" },
        { key:"geotextileFabric",   label:"Geotextile Fabric",          why:"Prevents base migration — often skipped" },
        { key:"edgeRestraint",      label:"Edge Restraint",             why:"Often omitted — causes edge spread over time" },
        { key:"jointSand",          label:"Joint Sand (polymeric?)",    why:"Regular sand washes out; polymeric is standard" },
      ]},
      { name:"Drainage & Details", fields:[
        { key:"gradingSlope",       label:"Grading & Slope",            why:"Critical for drainage away from foundation" },
        { key:"sealing",            label:"Initial Sealing",            why:"Extends life — often an add-on" },
        { key:"workmanshipWarranty",label:"Workmanship Warranty",       why:"Standard is 1–2 years" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
      ]},
    ],
    questions:[
      { text:"What's the base preparation — compacted gravel depth and type?", terms:["base"] },
      { text:"Are you including geotextile fabric under the base?", terms:["geotextile fabric"] },
      { text:"What material are you quoting — brand, product line, and thickness?", terms:[] },
      { text:"What type of joint sand — polymeric or regular?", terms:["polymeric sand"] },
      { text:"Are you including an edge restraint system?", terms:["edge restraint"] },
      { text:"Is grading and drainage included? Where does water drain?", terms:[] },
      { text:"Is initial sealing included?", terms:[] },
      { text:"What is your workmanship warranty?", terms:[] },
    ],
  },
  landscaping: {
    label:"Landscaping", icon:"🌿",
    categories:[
      { name:"Plant Material", fields:[
        { key:"plantMaterialSource",label:"Plant Material Source",      why:"'Labor only' bids look cheap — you still buy plants" },
        { key:"treeSizes",          label:"Tree Sizes & Species",       why:"15-gal vs. 5-gal = years of establishment difference" },
        { key:"shrubSizes",         label:"Shrub Sizes & Species",      why:"Container size tells you establishment timeline" },
        { key:"plantWarranty",      label:"Plant Warranty",             why:"Standard is 1 year with proper care" },
      ]},
      { name:"Irrigation", fields:[
        { key:"irrigationSystem",   label:"Irrigation Type",            why:"Drip superior for plants; spray for lawns" },
        { key:"smartController",    label:"Smart Controller",           why:"Saves 30–50% on water bills" },
        { key:"irrigationZones",    label:"Number of Zones",            why:"Under-zoning is a common shortcut" },
      ]},
      { name:"Ground Preparation", fields:[
        { key:"mulchType",          label:"Mulch Type & Depth",         why:"3\" standard; cedar vs. hardwood matters" },
        { key:"weedBarrier",        label:"Weed Barrier Fabric",        why:"Cheap fabric fails in 2 years" },
        { key:"edgingMaterial",     label:"Edging Material",            why:"Steel is premium; plastic fails quickly" },
      ]},
      { name:"Additional Scope", fields:[
        { key:"haulAway",           label:"Existing Material Removal",  why:"Clearing existing plants/debris: $500–$2,000" },
        { key:"paymentSchedule",    label:"Payment Schedule",           why:">50% upfront is a red flag" },
      ]},
    ],
    questions:[
      { text:"Are plants and materials included, or is it labor-only?", terms:[] },
      { text:"What size are the trees and shrubs — caliper for trees?", terms:["caliper","b&b"] },
      { text:"What irrigation system — drip or spray?", terms:["drip irrigation"] },
      { text:"Are you including a smart controller?", terms:["smart controller"] },
      { text:"What mulch type and depth?", terms:[] },
      { text:"Are you including weed barrier fabric?", terms:[] },
      { text:"What edging material?", terms:[] },
      { text:"What's the plant warranty and conditions?", terms:[] },
    ],
  },
};

function PreQuoteChecklist({ schema }) {
  const [checked, setChecked] = useState({});
  const done = Object.values(checked).filter(Boolean).length;
  const total = schema.questions.length;
  const pct = total > 0 ? Math.round((done/total)*100) : 0;

  const renderQuestion = (q) => {
    if (!q.terms?.length) return q.text;
    let parts = [q.text];
    q.terms.forEach(term => {
      parts = parts.flatMap(part => {
        if (typeof part !== "string") return [part];
        const idx = part.toLowerCase().indexOf(term.toLowerCase());
        if (idx === -1) return [part];
        const before = part.slice(0, idx);
        const matched = part.slice(idx, idx + term.length);
        const after = part.slice(idx + term.length);
        return [before, <Term key={term}>{matched}</Term>, after].filter(x => x !== "");
      });
    });
    return parts;
  };

  return (
    <div>
      <div style={{background:C.navy,borderRadius:13,padding:20,marginBottom:16,color:"#fff"}}>
        <div style={{fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:C.amber,marginBottom:5}}>Pre-Quote Checklist</div>
        <div style={{fontWeight:800,fontSize:"1.1rem",marginBottom:10}}>{schema.icon} {schema.label}</div>
        <div style={{background:"rgba(255,255,255,0.1)",borderRadius:8,padding:"10px 14px"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
            <span style={{fontSize:"0.76rem",color:"rgba(255,255,255,0.6)"}}>Questions covered</span>
            <span style={{fontWeight:700,color:C.amber}}>{pct}%</span>
          </div>
          <div style={{background:"rgba(255,255,255,0.15)",borderRadius:6,height:5}}>
            <div style={{width:`${pct}%`,height:"100%",background:C.amber,borderRadius:6,transition:"width 0.3s"}}/>
          </div>
        </div>
      </div>
      <div style={{...s.card,marginBottom:12,background:"#eff6ff",border:"1px solid #bfdbfe"}}>
        <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
          <span>💡</span>
          <div style={{fontSize:"0.81rem",color:"#1e40af",lineHeight:1.6}}>
            <strong>Tap any underlined term</strong> to get a plain-English explanation of what it means, why it matters, and what a good vs. bad answer looks like.
          </div>
        </div>
      </div>
      <div style={s.card}>
        <div style={{fontWeight:700,color:C.navy,marginBottom:3,fontSize:"0.9rem"}}>Ask every contractor these questions</div>
        <p style={{fontSize:"0.81rem",color:C.muted,marginBottom:16}}>Tap to check off as you go. Specific to {schema.label.toLowerCase()} projects.</p>
        {schema.questions.map((q,i)=>(
          <div key={i} style={{display:"flex",gap:11,padding:"12px 0",borderBottom:i<schema.questions.length-1?`1px solid ${C.border}`:"none",cursor:"pointer"}}>
            <div onClick={()=>setChecked(c=>({...c,[i]:!c[i]}))}
              style={{width:22,height:22,borderRadius:6,border:`2px solid ${checked[i]?C.green:C.border}`,background:checked[i]?C.green:"#fff",flexShrink:0,marginTop:2,
              display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s",cursor:"pointer"}}>
              {checked[i]&&<span style={{color:"#fff",fontSize:"0.75rem",fontWeight:700}}>✓</span>}
            </div>
            <div style={{fontSize:"0.87rem",lineHeight:1.6,color:C.navy,opacity:checked[i]?0.5:1,textDecoration:checked[i]?"line-through":"none"}}>
              {renderQuestion(q)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PdfUploader({ onTextExtracted, quoteIndex }) {
  const [status, setStatus] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "application/pdf") { setStatus("error"); return; }
    setStatus("loading");
    try {
  	const arrayBuffer = await file.arrayBuffer();
  	const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  	let fullText = "";
  	for (let i = 1; i <= pdf.numPages; i++) {
    	   try {
      		const page = await pdf.getPage(i);
      		const content = await page.getTextContent();
      		const pageText = content.items.map(item => item.str).join(" ");
      		if (pageText.trim().length > 0) {
        	  fullText += pageText + "\n";
      		}
    	} catch(pageErr) {
      	  // Skip pages that can't be read (image-only pages)
      	  continue;
    	}
  }
  console.log("Extracted text length:", fullText.trim().length, "Preview:", fullText.trim().substring(0, 200));
  if (fullText.trim().length < 50) {
    setStatus("error");
    return;
  }
  onTextExtracted(quoteIndex, fullText.trim());
  setStatus("done");
} catch(err) {
  setStatus("error");
}
  };

  return (
    <div style={{marginBottom:7}}>
      <label style={{display:"inline-flex",alignItems:"center",gap:7,background:"#f1f5f9",border:"1px dashed #94a3b8",borderRadius:8,padding:"7px 13px",cursor:"pointer",fontSize:"0.78rem",color:"#475569",fontWeight:600}}>
        <span>📄</span>
        {status==="loading"?"Reading PDF…":status==="done"?"✓ PDF loaded":status==="error"?"⚠ Try a different PDF":"Upload PDF instead"}
        <input type="file" accept="application/pdf" onChange={handleFile} style={{display:"none"}}/>
      </label>
    </div>
  );
}

function ComparisonTool({ schema, schemaKey }) {
  const [quotes, setQuotes] = useState([{name:"",text:""},{name:"",text:""}]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const resultRef = useRef(null);

  const updateQuote = (i,f,v)=>setQuotes(q=>q.map((x,idx)=>idx===i?{...x,[f]:v}:x));
  const addQuote = ()=>{ if(quotes.length<4) setQuotes(q=>[...q,{name:"",text:""}]); };
  const removeQuote = i=>{ if(quotes.length>2) setQuotes(q=>q.filter((_,idx)=>idx!==i)); };

  const analyze = async () => {
    const filled = quotes.filter(q=>q.text.trim().length>30);
    if(filled.length<2){setError("Please enter at least 2 quotes.");return;}
    setError(null); setLoading(true); setResult(null);

    const allFields = schema.categories.flatMap(cat=>cat.fields);
    const fieldKeys = allFields.map(f=>f.key).join(", ");
   const contractorList = filled.map((q,i)=>{
  const text = q.text;
  const startMarkers = [
    "master elite roof system",
    "roof system option",
    "scope of work",
    "description",
    "shingle material",
    "remove all existing",
    "tear off",
    "install gaf",
    "item\n",
    "estimate subtotal",
    "quote subtotal"
  ];
  let startIdx = 0;
  for (const marker of startMarkers) {
    const idx = text.toLowerCase().indexOf(marker);
    if (idx !== -1 && idx < text.length * 0.6) {
      startIdx = idx;
      break;
    }
  }
  const scopeText = text.slice(startIdx);
  return `QUOTE ${i+1} — ${q.name||`Contractor ${i+1}`}:\n${scopeText}`;
}).join("\n\n---\n\n");

    // Concise field template — one line per field keeps output small
    const fieldTemplate = allFields.map(f=>`"${f.key}":{"value":"<5 words max>","status":"specified|vague|missing","flag":null}`).join(",");

    const systemPrompt = `You are Level, a construction bid analysis expert. You extract structured data from contractor quotes to help homeowners compare them on a level playing field.

CRITICAL OUTPUT RULES:
- Respond ONLY with valid, complete JSON. No markdown, no prose outside the JSON.
- Keep ALL string values SHORT: 5 words max for field values, 15 words max for reasons/concerns.
- "flag" must be null or a ≤10-word string.
- "summary", "gradeReason" must each be ≤20 words.
- If a field is not mentioned in the quote, use "Not specified" as value with status "missing".
- You MUST close all JSON brackets. Never leave the JSON incomplete.
- If a quote contains multiple options, analyze the BASE option only. List each upgrade tier with its price in topConcerns (e.g., "Gold upgrade available: $X more").
- Ignore legal disclaimers, warranty legalese, contract terms, and payment financing details - focus only on scope and materials.

GRADING RULES — two independent dimensions per contractor:

GRADE (quote completeness — did they give enough info to evaluate?):
- A: Nearly all key fields specified, pricing clear, scope well-defined.
- B: Most fields present, minor gaps, pricing included.
- C: Significant fields missing, pricing unclear or estimated.
- D: Too incomplete to evaluate — missing pricing, scope, or major material specs.

COMPLETENESS badge:
- "complete" → grade A or B
- "partial" → grade C
- "incomplete" → grade D

VALUE RATING (how the offer compares on price and scope — independent of completeness):
- "best_value" → competitive price with solid scope and materials
- "premium" → higher price justified by superior warranty, materials, or scope
- "standard" → mid-range price, typical scope
- "caution" → pricing concern, missing scope, or something worth flagging`;

    const prompt = `PROJECT: ${schema.label}
QUOTES:
${contractorList}

Analyze the quotes above. Extract these field keys for each contractor: ${fieldKeys}

Return ONLY this JSON structure (keep all strings short per system rules):
{
  "summary":"<20 words — neutral overview of all quotes submitted, no winner>",
  "redFlags":[{"contractor":"name","flag":"<10 words>","severity":"high|medium|low"}],
  "followUpQuestions":[{"contractor":"name","question":"<15 words>"}],
  "contractors":[{
    "name":"string",
    "totalQuoted":0,
    "estimatedTrue":0,
    "grade":"A|B|C|D",
    "gradeReason":"<15 words — explain quote completeness and detail level>",
    "completeness":"complete|partial|incomplete",
    "valueRating":"best_value|premium|standard|caution",
    "topConcerns":["<8 words each, max 3 items — missing info or unclear scope>"],
    "fields":{${fieldTemplate}}
  }]
}`;

    try {
      const res = await fetch("/api/analyze", {
  			method: "POST",
 			headers: { "Content-Type": "application/json" },
  			body: JSON.stringify({
    				model: "claude-sonnet-4-5",
    				max_tokens: 8000,
    				system: systemPrompt,
    				messages: [{ role: "user", content: prompt }]
  			})
	});

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error.message || "API error");
      }

      const raw = data.content?.find(b => b.type === "text")?.text || "";

      // Strip any accidental markdown fences
      const cleaned = raw.replace(/^```json\s*/i, "").replace(/^```\s*/i, "").replace(/```\s*$/i, "").trim();

      let parsed;
      try {
        parsed = JSON.parse(cleaned);
      } catch (jsonErr) {
        // Try to extract just the JSON object if there's surrounding text
        const match = cleaned.match(/\{[\s\S]*\}/);
        if (match) {
          parsed = JSON.parse(match[0]);
        } else {
          throw new Error("Response was not valid JSON. Try again — the quotes may be too long.");
        }
      }

      setResult(parsed);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);

    } catch(e) {
      setError("Analysis failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const compColor=r=>({complete:"green",partial:"yellow",incomplete:"red"}[r]||"gray");
  const compLabel=r=>({complete:"Complete",partial:"Partial",incomplete:"Incomplete"}[r]||r);
  const valColor=r=>({best_value:"green",premium:"yellow",standard:"gray",caution:"red"}[r]||"gray");
  const valLabel=r=>({best_value:"Best Value",premium:"Premium",standard:"Standard",caution:"⚠ Caution"}[r]||r);
  const gradeColor=g=>({A:"green",B:"green",C:"yellow",D:"red"}[g]||"gray");
  const statusColor=st=>({specified:"green",vague:"yellow",missing:"red"}[st]||"gray");
  const statusIcon=st=>({specified:"✓",vague:"~",missing:"⚠"}[st]||"—");
  const sevColor=sv=>({high:"red",medium:"yellow",low:"gray"}[sv]||"gray");

  return (
    <div>
      <div style={s.card}>
        <div style={s.h2}>Compare {schema.label} Quotes</div>
        <p style={s.sub}>Paste each quote exactly as received. Level extracts {schema.categories.flatMap(c=>c.fields).length} fields specific to {schema.label.toLowerCase()} projects.</p>
        {quotes.map((q,i)=>(
          <div key={i} style={{marginBottom:13,padding:14,background:C.bg,borderRadius:11,border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:"0.88rem",color:C.navy}}>Quote {i+1}</div>
              {quotes.length>2&&<button style={{...s.btnGhost,padding:"3px 9px",fontSize:"0.7rem",color:C.red,borderColor:"#fecaa0"}} onClick={()=>removeQuote(i)}>Remove</button>}
            </div>
            <input value={q.name} onChange={e=>updateQuote(i,"name",e.target.value)} style={{...s.input,marginBottom:7}} placeholder="Contractor name"/>
            <PdfUploader quoteIndex={i} onTextExtracted={(idx,text)=>updateQuote(idx,"text",text)}/>
<textarea value={q.text} onChange={e=>updateQuote(i,"text",e.target.value)} style={{...s.textarea,minHeight:100}} placeholder="Paste the full quote here, or upload a PDF above…"/>
          </div>
        ))}
        <div style={{display:"flex",gap:9,flexWrap:"wrap"}}>
          {quotes.length<4&&<button style={s.btnGhost} onClick={addQuote}>+ Add quote</button>}
          <button style={{...s.btn,marginLeft:"auto",opacity:loading?0.7:1}} onClick={analyze} disabled={loading}>
            {loading?"Analyzing…":"Analyze Quotes →"}
          </button>
        </div>
        {error&&<div style={{marginTop:10,background:"#fee2e2",color:"#991b1b",padding:"9px 13px",borderRadius:8,fontSize:"0.81rem"}}>{error}</div>}
      </div>

      {loading&&(
        <div style={{...s.card,textAlign:"center",padding:32}}>
          <div style={{fontSize:"1.6rem",marginBottom:8}}>🔍</div>
          <div style={{fontWeight:700,color:C.navy,marginBottom:4}}>Analyzing {schema.label.toLowerCase()} quotes…</div>
          <div style={{color:C.muted,fontSize:"0.81rem"}}>Extracting {schema.categories.flatMap(c=>c.fields).length} project-specific fields</div>
          <style>{`@keyframes pulse{0%,100%{opacity:0.3;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}`}</style>
          <div style={{marginTop:14,display:"flex",justifyContent:"center",gap:6}}>
            {[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:C.amber,animation:`pulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
          </div>
        </div>
      )}

      {result&&(
        <div ref={resultRef}>
          <div style={{background:C.navy,borderRadius:13,padding:20,marginBottom:16,color:"#fff"}}>
            <div style={{fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:C.amber,marginBottom:8}}>Quote Analysis</div>
            <p style={{color:"rgba(255,255,255,0.85)",fontSize:"0.85rem",lineHeight:1.7,margin:"0 0 10px"}}>{result.summary}</p>
            <p style={{color:"rgba(255,255,255,0.45)",fontSize:"0.75rem",lineHeight:1.6,margin:0}}>This analysis is for informational purposes only. Review each quote carefully and consult a professional before making any decision.</p>
          </div>

          <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fit,minmax(150px,1fr))`,gap:11,marginBottom:18}}>
            {result.contractors?.map((c,i)=>(
              <div key={i} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:11,padding:14,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:c.completeness==="complete"?C.green:c.completeness==="incomplete"?C.red:C.yellow}}/>
                <div style={{fontWeight:700,fontSize:"0.85rem",color:C.navy,marginBottom:5}}>{c.name}</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:7}}>
                  <span style={s.badge(gradeColor(c.grade))}>Grade {c.grade}</span>
                  <span style={s.badge(compColor(c.completeness))}>{compLabel(c.completeness)}</span>
                  {c.valueRating&&<span style={s.badge(valColor(c.valueRating))}>{valLabel(c.valueRating)}</span>}
                </div>
                <div style={{fontSize:"0.72rem",color:C.muted}}>Quoted</div>
                <div style={{fontWeight:800,fontSize:"1.1rem",color:C.navy}}>${c.totalQuoted?.toLocaleString()}</div>
                {c.estimatedTrue!==c.totalQuoted&&<>
                  <div style={{fontSize:"0.68rem",color:C.muted,marginTop:3}}>True est. (w/ gaps)</div>
                  <div style={{fontWeight:700,color:C.red,fontSize:"0.88rem"}}>${c.estimatedTrue?.toLocaleString()}</div>
                </>}
                <div style={{marginTop:7,fontSize:"0.73rem",color:C.muted,lineHeight:1.5}}>{c.gradeReason}</div>
                {c.topConcerns?.length>0&&(
                  <div style={{marginTop:9,paddingTop:9,borderTop:`1px solid ${C.border}`}}>
                    {c.topConcerns.map((concern,j)=>(
                      <div key={j} style={{fontSize:"0.72rem",color:"#be123c",marginBottom:3}}>⚠ {concern}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:13,overflow:"hidden",marginBottom:16}}>
            <div style={{display:"flex",overflowX:"auto",borderBottom:`1px solid ${C.border}`,background:C.bg}}>
              {schema.categories.map((cat,ci)=>(
                <button key={ci} onClick={()=>setActiveCategory(ci)} style={{
                  padding:"11px 14px",border:"none",cursor:"pointer",fontWeight:700,fontSize:"0.78rem",
                  whiteSpace:"nowrap",background:"transparent",
                  color:activeCategory===ci?C.navy:C.muted,
                  borderBottom:activeCategory===ci?`2px solid ${C.amber}`:"2px solid transparent",
                }}>{cat.name}</button>
              ))}
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.8rem"}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    <th style={{padding:"9px 14px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,minWidth:150}}>Field</th>
                    {result.contractors?.map((c,i)=><th key={i} style={{padding:"9px 14px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,minWidth:150}}>{c.name}</th>)}
                    <th style={{padding:"9px 14px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:"0.68rem",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,minWidth:180}}>Why It Matters</th>
                  </tr>
                </thead>
                <tbody>
                  {schema.categories[activeCategory]?.fields.map((field,fi)=>(
                    <tr key={fi} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:"10px 14px",fontWeight:700,color:C.navy,fontSize:"0.82rem",verticalAlign:"top"}}>
                        {GLOSSARY[field.label.toLowerCase()] ? <Term>{field.label}</Term> : field.label}
                      </td>
                      {result.contractors?.map((c,ci)=>{
                        const cell=c.fields?.[field.key];
                        const st=cell?.status||"missing";
                        return(
                          <td key={ci} style={{padding:"10px 14px",verticalAlign:"top"}}>
                            <div style={{display:"flex",alignItems:"flex-start",gap:6}}>
                              <span style={{...s.badge(statusColor(st)),flexShrink:0,marginTop:1}}>{statusIcon(st)}</span>
                              <div>
                                <div style={{fontSize:"0.82rem",color:C.text,lineHeight:1.5}}>{cell?.value||"Not specified"}</div>
                                {cell?.flag&&<div style={{marginTop:4,fontSize:"0.72rem",color:"#be123c"}}>⚠ {cell.flag}</div>}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                      <td style={{padding:"10px 14px",fontSize:"0.75rem",color:C.muted,lineHeight:1.5,verticalAlign:"top"}}>{field.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {result.redFlags?.length>0&&(
            <div style={{...s.card,marginBottom:14}}>
              <div style={{fontWeight:700,color:C.navy,marginBottom:3,fontSize:"0.9rem"}}>🚩 Red Flags</div>
              <div style={{color:C.muted,fontSize:"0.8rem",marginBottom:14}}>Resolve these before signing anything.</div>
              {result.redFlags.map((f,i)=>(
                <div key={i} style={{borderLeft:`3px solid ${f.severity==="high"?C.red:f.severity==="medium"?C.yellow:C.muted}`,paddingLeft:12,marginBottom:12}}>
                  <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:3}}>
                    <span style={{fontWeight:700,fontSize:"0.8rem",color:C.navy}}>{f.contractor}</span>
                    <span style={s.badge(sevColor(f.severity))}>{f.severity}</span>
                  </div>
                  <div style={{fontSize:"0.83rem",color:C.text,lineHeight:1.6}}>{f.flag}</div>
                </div>
              ))}
            </div>
          )}

          {result.followUpQuestions?.length>0&&(
            <div style={s.card}>
              <div style={{fontWeight:700,color:C.navy,marginBottom:3,fontSize:"0.9rem"}}>💬 Questions to Ask Before You Sign</div>
              <div style={{color:C.muted,fontSize:"0.8rem",marginBottom:14}}>Send these to fill gaps in each quote.</div>
              {result.contractors?.map(c=>{
                const qs=result.followUpQuestions?.filter(q=>q.contractor===c.name);
                if(!qs?.length) return null;
                return(<div key={c.name} style={{marginBottom:16}}>
                  <div style={{fontWeight:700,color:C.navy,marginBottom:7,fontSize:"0.85rem"}}>{c.name}</div>
                  {qs.map((q,i)=><div key={i} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 13px",marginBottom:5,fontSize:"0.83rem",lineHeight:1.6}}>💬 {q.question}</div>)}
                </div>);
              })}
            </div>
          )}

          <div style={{textAlign:"center",paddingBottom:16}}>
            <button style={s.btnGhost} onClick={()=>{setResult(null);window.scrollTo({top:0,behavior:"smooth"});}}>← Compare different quotes</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Level() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [view, setView] = useState("checklist");
  const schema = selectedProject ? SCHEMAS[selectedProject] : null;

  const NAV = [
    {id:"checklist", label:"📋 Pre-Quote Prep"},
    {id:"compare",   label:"📊 Compare Quotes"},
    {id:"benchmarks",label:"💰 Cost Guide"},
  ];

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.logo}>LEVEL</div>
        <div style={{color:"rgba(255,255,255,0.45)",fontSize:"0.78rem",marginLeft:4}}>· {schema?`${schema.icon} ${schema.label}`:"Contractor Intelligence"}</div>
        {selectedProject&&(
          <button style={{...s.btnGhost,marginLeft:"auto",color:"rgba(255,255,255,0.6)",borderColor:"rgba(255,255,255,0.2)",fontSize:"0.73rem"}}
            onClick={()=>{setSelectedProject(null);setView("checklist");}}>← Change Project</button>
        )}
      </div>

      <div style={{maxWidth:860,margin:"0 auto",padding:"20px 14px"}}>
        {!selectedProject&&(
          <div>
            <div style={{textAlign:"center",marginBottom:28}}>
              <div style={{fontWeight:900,fontSize:"1.5rem",color:C.navy,marginBottom:8}}>What project are you getting quotes for?</div>
              <div style={{color:C.muted,fontSize:"0.88rem"}}>Level uses project-specific criteria — not a generic checklist.</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:13}}>
              {Object.keys(SCHEMAS).map(key=>{
                const sc=SCHEMAS[key];
                return(
                  <div key={key} onClick={()=>setSelectedProject(key)}
                    style={{background:"#fff",border:`2px solid ${C.border}`,borderRadius:14,padding:22,cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=C.amber;e.currentTarget.style.transform="translateY(-2px)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform="none";}}>
                    <div style={{fontSize:"2rem",marginBottom:10}}>{sc.icon}</div>
                    <div style={{fontWeight:800,fontSize:"0.95rem",color:C.navy,marginBottom:5}}>{sc.label}</div>
                    <div style={{fontSize:"0.73rem",color:C.muted}}>{sc.categories.flatMap(c=>c.fields).length} comparison fields · {sc.questions.length} walkthrough questions</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {selectedProject&&schema&&(
          <>
            <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:11,marginBottom:18,display:"flex",overflow:"hidden"}}>
              {NAV.map(({id,label})=>(
                <button key={id} onClick={()=>setView(id)} style={{
                  flex:1,padding:"13px 6px",border:"none",cursor:"pointer",fontWeight:700,fontSize:"0.8rem",
                  background:view===id?C.navy:"transparent",
                  color:view===id?"#fff":C.muted,transition:"all 0.2s",
                }}>{label}</button>
              ))}
            </div>
            {view==="checklist"&&<PreQuoteChecklist schema={schema}/>}
            {view==="compare"&&<ComparisonTool schema={schema} schemaKey={selectedProject}/>}
            {view==="benchmarks"&&<CostBenchmarks schemaKey={selectedProject}/>}
          </>
        )}
      </div>
    </div>
  );
}